import * as React from "react"

import { Layout } from "src/components/organisms/Layout";
import { Promo } from "src/components/organisms/Promo";
import {ProductsGrid} from "src/components/organisms/ProductsGrid";
import {FAQ} from "src/components/organisms/FAQ";
import {LibericaFree} from "src/components/organisms/LibericaFree/LibericaFree";
import {Advantages} from "src/components/organisms/Advantages";
import * as styles from "src/components/organisms/Advantages/styles.module.scss";
import {Props as CardProps} from "src/components/organisms/ProductCard";
import {BlockHeading} from "src/components/organisms/BlockHeading";
import {ContactsForm} from "src/components/organisms/ContactsForm";

import { graphql, type HeadFC } from "gatsby";
import type { GatsbyTypes } from "gatsby-types";
import Img from "gatsby-image";
import {gatsbyImagePlaceholderStyle} from "util/commons";

import icon1 from 'images/pages/liberica-native-image-kit/optimize.svg';
import icon2 from 'images/pages/liberica-native-image-kit/discover.svg';

import {MockFaqData} from "data/liberica-native-image-kit/data/faqData";
import {Items, LeftBlock, Buttons} from "data/liberica-native-image-kit/data/libericaFreeData";
import {products} from "data/liberica-native-image-kit/data/productsData";
import {formItems} from "data/common/formsData";
import {BackgroundImage} from "src/components/organisms/BackgroundImage";
import {StaticImage} from "gatsby-plugin-image";
import {useEffect} from "react";
import {appendLeadInfoScript} from "util/ga/gaHelpers";
import * as formStyles from "src/data/common/forms.module.scss";

type Item = CardProps & { id: number }

export const cards2: Item[]  = [
    {
        id: 2,
        icon: icon1,
        title: 'Optimize and minimize',
        description: 'Liberica Native Image Kit is a utility capable of converting your JVM-based application into a fully compiled native executable ahead-of-time under the closed-world assumption with an almost instant startup time. It optimizes resource consumption, minimizes the static footprint, and works on various platforms, including lightweight musl-based Alpine Linux.',
        className: styles.card,
        // TODO: get button back when link is ready
        // button: {
        //     href: "",
        //     caption: "Download white paper"
        // },
        childrenClassNames: {
            title: 'color-white',
            description: 'color-gray',
            button: 'bg-pink color-dark',
        },
    },
    {
        id: 3,
        icon: icon2,
        title: 'Discover the possibilities',
        description: <>
                <p className={'color-gray'}>Liberica Native Image Kit allows seamless polyglot projects, such as microservices in different programming languages. Experience native image optimization: lesser RAM consumption, easy communication, and high speed of execution.</p>
                <br/>
                <p className={'color-gray'}>Developing with Liberica Native Image Kit equals efficiency and expert support from BellSoft engineers.</p>
            </>,
        className: styles.card,
        childrenClassNames: {
            title: 'color-white',
            description: 'color-gray',
        },
    }
]

interface Props {
    data: GatsbyTypes.Query
}

const IndexPage: React.FC<Props> = ({ data }) => {

    const description = <p className={"color-white"}>Liberica NIK is a multilingual tool that transforms your applications into performant native images{"\n\n"}Based on <a className={"link"} href={"https://www.graalvm.org/community/opensource/"}>GraalVM Open Source</a></p>

    const promoBackground = (className: string) => {
        const sources = [
            {
                ...data.promoDesktopImage.childImageSharp.fluid,
                media: `(min-width: 1024px)`,
            },
            {
                ...data.promoTabletImage.childImageSharp.fluid,
                media: `(min-width: 768px)`,
            },
            data.promoMobileImage.childImageSharp.fluid,
        ]

        return (
            <Img className={className} fluid={sources} placeholderStyle={gatsbyImagePlaceholderStyle} />
        )
    }

    return (
        <Layout button ={{
            className: 'bg-pink color-dark'
        }} forms={data.allFormsJson.nodes} popupType="nik">
            <Promo
                className="bg-black"
                style={{ maxWidth: '720px' }}
                backgroundElement={promoBackground}
                title={"Liberica \nNative Image Kit"}
                subtitle="A versatile tool for accelerating your applications."
                description={description}
                buttons={[
                    {
                        caption: 'Request a Quote',
                        href: "javascript:void(0)",
                        modalId: '#popup-request-native-image-kit-quote',
                        className: 'bg-pink color-dark'
                    },
                    {
                        caption: 'Download center',
                        href: '/pages/downloads/native-image-kit/',
                        className: 'bg-white color-dark'
                    }
                ]}
            />

            <Advantages items={cards2} />

            <BlockHeading text={"Features and benefits"}>
                <ProductsGrid
                    columns={2}
                    items={products}
                />
            </BlockHeading>


            <FAQ items={MockFaqData.items} scrollable={false} stickyMenu={true}/>

            <LibericaFree buttons={Buttons} leftBlock={LeftBlock} items={Items} />

            <a id="contacts-form"></a>
            <BackgroundImage
                desktop={<StaticImage src={"../../images/pages/liberica-native-image-kit/form-desktop.png"} alt={"test"}/>}
                tablet={<StaticImage src={"../../images/pages/liberica-native-image-kit/form-tablet.png"} alt={"test"}/>}
                mobile={<StaticImage src={"../../images/pages/liberica-native-image-kit/form-mobile.png"} alt={"test"}/>}
                alt={"Contact Form Background"}
            >
                <ContactsForm
                    details="Get guidance on migrating to Liberica JDK"
                    title="Speak with an expert to see which solution will serve your business the best"

                    successDetails="Thank you for your interest in our company"
                    successTitle="We will send the guidance on migrating Liberica JDK to the specified email"
                    form={formItems} classNames={{ input: {container: formStyles.nikForm}}}
                />
            </BackgroundImage>
        </Layout>
    )
}

export default IndexPage

export const Head: HeadFC = () =>
    <>
        <title>Liberica Native Image Kit | BellSoft Java</title>
        <meta name="description" content="Liberica Native Image Kit (Liberica NIK) is an open source tool for accelerating JVM software. It converts Java bytecode into an optimized native executable." />
    </>

export const query = graphql`
query {
    promoMobileImage: file(absolutePath: {regex: "/.*images\\/pages\\/liberica-native-image-kit\\/promo-mobile.png/"}) {
    childImageSharp {
        fluid(fit: COVER, pngQuality: 90, webpQuality: 90, quality: 90, maxWidth: 1280) {
            ...GatsbyImageSharpFluid,
        }
    }
    }
    promoTabletImage: file(
    absolutePath: {regex: "/.*images\\/pages\\/liberica-native-image-kit\\/promo-tablet.png/"}
    ) {
    childImageSharp {
        fluid(fit: COVER, pngQuality: 90, webpQuality: 90, quality: 90, maxWidth: 1280) {
            ...GatsbyImageSharpFluid,
        }
    }
    }
    promoDesktopImage: file(
    absolutePath: {regex: "/.*images\\/pages\\/liberica-native-image-kit\\/promo-desktop.png/"}
    ) {
    childImageSharp {
        fluid(fit: COVER, pngQuality: 90, webpQuality: 90, quality: 90, maxWidth: 1280) {
            ...GatsbyImageSharpFluid,
        }
    }
    }

  allFormsJson(filter: {fields: {idSelector: {eq: "popup-request-native-image-kit-quote"}}}) {
    nodes {
      title
      details
      successDescription
      successDetails
      successTitle
      formId
      eventAction
      fields {
        hashedIdSelector
        idSelector
      }
      items {
        gridColumn
        name
        placeholder
        required
        type
      }
      controls {
        terms {
          name
        }
        button {
          caption
        }
      }
    }
  }
}
`
